import { useEffect, useRef, useState } from 'react';
import { HIGHLIGHT_COLORS } from '@td/shared';

/**
 * A small formatting-capable field.
 *
 * Uses `contentEditable` with `document.execCommand`. That API is formally
 * deprecated but is still the only thing every browser implements
 * consistently, and pulling in a full editor framework for bold/italic/
 * highlight would be far more code than the feature warrants.
 *
 * Whatever this produces is re-sanitised on the server before storage — the
 * allowlist there, not this component, is what makes the markup safe.
 */
export function RichTextField({
  value,
  onChange,
  placeholder,
  minHeight = 56,
  label,
}: {
  value: string;
  onChange: (html: string) => void;
  placeholder?: string;
  minHeight?: number;
  label: string;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [focused, setFocused] = useState(false);
  const [showColors, setShowColors] = useState(false);

  // Only write into the DOM when the incoming value genuinely differs, or the
  // caret jumps to the start on every keystroke.
  useEffect(() => {
    const el = ref.current;
    if (el && el.innerHTML !== value) el.innerHTML = value || '';
  }, [value]);

  function exec(command: string, arg?: string) {
    ref.current?.focus();
    // Emit tags rather than inline styles wherever the browser offers a choice.
    document.execCommand('styleWithCSS', false, command === 'hiliteColor' ? 'true' : 'false');
    document.execCommand(command, false, arg);
    onChange(ref.current?.innerHTML ?? '');
  }

  const isEmpty = !value || value === '<br>' || value === '<p><br></p>';

  const Btn = ({
    cmd,
    arg,
    title,
    children,
  }: {
    cmd: string;
    arg?: string;
    title: string;
    children: React.ReactNode;
  }) => (
    <button
      type="button"
      // Keeps the text selection alive; a normal click would blur the field
      // first and the command would apply to nothing.
      onMouseDown={(e) => e.preventDefault()}
      onClick={() => exec(cmd, arg)}
      title={title}
      aria-label={title}
      className="h-6 w-6 rounded text-xs text-walnut transition-colors hover:bg-sand/60"
    >
      {children}
    </button>
  );

  return (
    <div>
      <div
        className={[
          'mb-1 flex items-center gap-0.5 rounded-lg border px-1 py-0.5 transition-colors',
          focused ? 'border-clay bg-linen/60' : 'border-transparent',
        ].join(' ')}
      >
        <Btn cmd="bold" title="Bold">
          <span className="font-bold">B</span>
        </Btn>
        <Btn cmd="italic" title="Italic">
          <span className="italic">I</span>
        </Btn>
        <Btn cmd="underline" title="Underline">
          <span className="underline">U</span>
        </Btn>

        <span className="mx-0.5 h-4 w-px bg-sand" />

        <div className="relative">
          <button
            type="button"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => setShowColors((v) => !v)}
            title="Highlight"
            aria-label="Highlight"
            className="flex h-6 w-6 items-center justify-center rounded text-xs transition-colors hover:bg-sand/60"
          >
            <span className="rounded-sm px-1 text-espresso" style={{ backgroundColor: '#FBEFC8' }}>
              H
            </span>
          </button>

          {showColors && (
            <div className="absolute left-0 top-7 z-10 flex gap-1 rounded-lg border border-sand bg-white p-1.5 shadow-lifted">
              {HIGHLIGHT_COLORS.map((c) => (
                <button
                  key={c.value}
                  type="button"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => {
                    exec('hiliteColor', c.value);
                    setShowColors(false);
                  }}
                  title={c.name}
                  aria-label={`Highlight ${c.name}`}
                  className="h-5 w-5 rounded border border-sand"
                  style={{ backgroundColor: c.value }}
                />
              ))}
              <button
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => {
                  exec('hiliteColor', 'transparent');
                  setShowColors(false);
                }}
                title="Remove highlight"
                aria-label="Remove highlight"
                className="h-5 w-5 rounded border border-sand text-[10px] text-stone"
              >
                ✕
              </button>
            </div>
          )}
        </div>

        <span className="mx-0.5 h-4 w-px bg-sand" />

        <Btn cmd="insertUnorderedList" title="Bulleted list">
          ☰
        </Btn>
        <Btn cmd="insertOrderedList" title="Numbered list">
          ⒈
        </Btn>

        <span className="mx-0.5 h-4 w-px bg-sand" />

        <Btn cmd="removeFormat" title="Clear formatting">
          ⌫
        </Btn>
      </div>

      <div className="relative">
        <div
          ref={ref}
          contentEditable
          role="textbox"
          aria-multiline="true"
          aria-label={label}
          suppressContentEditableWarning
          onInput={() => onChange(ref.current?.innerHTML ?? '')}
          onFocus={() => setFocused(true)}
          onBlur={() => {
            setFocused(false);
            setShowColors(false);
          }}
          // Paste as plain text: pasting from Word or a website would
          // otherwise drag in fonts, colours and spans the sanitiser strips
          // anyway, so the formatting would silently vanish on save.
          onPaste={(e) => {
            e.preventDefault();
            const text = e.clipboardData.getData('text/plain');
            document.execCommand('insertText', false, text);
            onChange(ref.current?.innerHTML ?? '');
          }}
          onKeyDown={(e) => {
            if (!(e.metaKey || e.ctrlKey)) return;
            const k = e.key.toLowerCase();
            if (k === 'b' || k === 'i' || k === 'u') {
              e.preventDefault();
              exec(k === 'b' ? 'bold' : k === 'i' ? 'italic' : 'underline');
            }
          }}
          className="rich-text field resize-y overflow-y-auto"
          style={{ minHeight }}
        />
        {isEmpty && placeholder && (
          <span className="pointer-events-none absolute left-3 top-2 text-sm text-stone">
            {placeholder}
          </span>
        )}
      </div>
    </div>
  );
}
